import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

// Admin Order Lifecycle Service
export const adminOrderLifecycleService = {
  async getAll(filters = {}) {
    let query = supabase
      ?.from('inquiries')
      ?.select(`
        *,
        buyer:user_profiles!inquiries_buyer_id_fkey(
          id, email, full_name, company_name, phone
        ),
        bean:beans(
          id, name_en, name_zh, price_usd_per_kg,
          supplier:suppliers(id, name_en, name_zh, contact_email, contact_phone)
        ),
        confirmed_by_user:user_profiles!inquiries_confirmed_by_fkey(
          id, full_name
        ),
        payment_verified_by_user:user_profiles!inquiries_payment_verified_by_fkey(
          id, full_name
        ),
        delivery_confirmed_by_user:user_profiles!inquiries_delivery_confirmed_by_fkey(
          id, full_name
        ),
        admin_assigned_to_user:user_profiles!inquiries_admin_assigned_to_fkey(
          id, full_name, email
        )
      `)
      ?.order('created_at', { ascending: false });

    if (filters?.status && filters?.status !== 'all') {
      query = query?.eq('status', filters?.status);
    }

    if (filters?.priority && filters?.priority !== 'all') {
      query = query?.eq('priority', filters?.priority);
    }

    if (filters?.adminAssignedTo) {
      query = query?.eq('admin_assigned_to', filters?.adminAssignedTo);
    }

    const { data, error } = await query;
    if (error) throw error;
    return toCamelCase(data);
  },

  async getById(orderId) {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.select(`
        *,
        buyer:user_profiles!inquiries_buyer_id_fkey(
          id, email, full_name, company_name, phone, address, city, country
        ),
        bean:beans(
          id, name_en, name_zh, price_usd_per_kg, description_en, description_zh,
          supplier:suppliers(id, name_en, name_zh, contact_email, contact_phone)
        ),
        confirmed_by_user:user_profiles!inquiries_confirmed_by_fkey(
          id, full_name
        ),
        payment_verified_by_user:user_profiles!inquiries_payment_verified_by_fkey(
          id, full_name
        ),
        delivery_confirmed_by_user:user_profiles!inquiries_delivery_confirmed_by_fkey(
          id, full_name
        ),
        admin_assigned_to_user:user_profiles!inquiries_admin_assigned_to_fkey(
          id, full_name, email
        )
      `)
      ?.eq('id', orderId)
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async confirmOrder(orderId, notes = '') {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update({
        status: 'confirmed',
        confirmed_at: new Date()?.toISOString(),
        confirmed_by: user?.id,
        supplier_notes: notes
      })
      ?.eq('id', orderId)
      ?.select();

    if (error) throw error;

    // Create timeline event
    await this?.createTimelineEvent(orderId, 'order_confirmed', 'Order Confirmed', notes);

    return data && data?.length > 0 ? toCamelCase(data?.[0]) : null;
  },

  async updatePaymentStatus(orderId, paymentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData = toSnakeCase({
      status: paymentData?.status,
      paymentMethod: paymentData?.paymentMethod,
      paymentReference: paymentData?.paymentReference,
      paymentAmountUsd: paymentData?.paymentAmountUsd,
      paymentNotes: paymentData?.paymentNotes
    });

    if (paymentData?.status === 'payment_verified') {
      updateData.payment_verified_at = new Date()?.toISOString();
      updateData.payment_verified_by = user?.id;
    }

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update(updateData)
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create timeline event
    const eventTitle = paymentData?.status === 'payment_verified' 
      ? 'Payment Verified' 
      : paymentData?.status === 'payment_failed' ?'Payment Failed' :'Payment Pending';
    await this?.createTimelineEvent(orderId, paymentData?.status, eventTitle, paymentData?.paymentNotes);

    return toCamelCase(data);
  },

  async scheduleShipment(orderId, shipmentData) {
    const updateData = toSnakeCase({
      status: 'shipment_scheduled',
      shipmentScheduledDate: shipmentData?.shipmentScheduledDate,
      shipmentMethod: shipmentData?.shipmentMethod,
      trackingNumber: shipmentData?.trackingNumber,
      carrier: shipmentData?.carrier,
      estimatedDeliveryDate: shipmentData?.estimatedDeliveryDate
    });

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update(updateData)
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create timeline event
    await this?.createTimelineEvent(
      orderId, 
      'shipment_scheduled', 
      'Shipment Scheduled', 
      `Carrier: ${shipmentData?.carrier}, Tracking: ${shipmentData?.trackingNumber}`
    );

    return toCamelCase(data);
  },

  async updateShipmentStatus(orderId, status, notes = '') {
    const updateData = { status };

    if (status === 'delivered') {
      const { data: { user } } = await supabase?.auth?.getUser();
      updateData.actual_delivery_date = new Date()?.toISOString();
      updateData.delivery_confirmed_by = user?.id;
      updateData.delivery_notes = notes;
    }

    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update(updateData)
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create timeline event
    const eventTitle = status === 'in_transit' ? 'In Transit' : 'Delivered';
    await this?.createTimelineEvent(orderId, status, eventTitle, notes);

    return toCamelCase(data);
  },

  async openDispute(orderId, disputeReason) {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update({
        status: 'disputed',
        dispute_reason: disputeReason,
        dispute_opened_at: new Date()?.toISOString()
      })
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create timeline event
    await this?.createTimelineEvent(orderId, 'dispute_opened', 'Dispute Opened', disputeReason);

    return toCamelCase(data);
  },

  async resolveDispute(orderId, resolution) {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update({
        status: 'resolved',
        dispute_resolution: resolution,
        dispute_resolved_at: new Date()?.toISOString()
      })
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;

    // Create timeline event
    await this?.createTimelineEvent(orderId, 'dispute_resolved', 'Dispute Resolved', resolution);

    return toCamelCase(data);
  },

  async updatePriority(orderId, priority) {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update({ priority })
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async assignAdmin(orderId, adminId) {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.update({ admin_assigned_to: adminId })
      ?.eq('id', orderId)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getTimelineEvents(orderId) {
    const { data, error } = await supabase
      ?.from('order_lifecycle_events')
      ?.select(`
        *,
        performed_by_user:user_profiles(id, full_name)
      `)
      ?.eq('order_id', orderId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async createTimelineEvent(orderId, eventType, eventTitle, eventDescription = '') {
    const { data: { user } } = await supabase?.auth?.getUser();

    const { data, error } = await supabase
      ?.from('order_lifecycle_events')
      ?.insert({
        order_id: orderId,
        event_type: eventType,
        event_title: eventTitle,
        event_description: eventDescription,
        performed_by: user?.id
      })
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async getDocuments(orderId) {
    const { data, error } = await supabase
      ?.from('order_documents')
      ?.select(`
        *,
        uploaded_by_user:user_profiles(id, full_name)
      `)
      ?.eq('order_id', orderId)
      ?.order('uploaded_at', { ascending: false });

    if (error) throw error;
    return toCamelCase(data);
  },

  async uploadDocument(orderId, documentData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const insertData = toSnakeCase({
      orderId,
      documentType: documentData?.documentType,
      documentName: documentData?.documentName,
      fileUrl: documentData?.fileUrl,
      fileSizeBytes: documentData?.fileSizeBytes,
      notes: documentData?.notes,
      uploadedBy: user?.id
    });

    const { data, error } = await supabase
      ?.from('order_documents')
      ?.insert(insertData)
      ?.select()
      ?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async deleteDocument(documentId) {
    const { error } = await supabase
      ?.from('order_documents')
      ?.delete()
      ?.eq('id', documentId);

    if (error) throw error;
  },

  async getStatistics() {
    const { data, error } = await supabase
      ?.from('inquiries')
      ?.select('status, priority');

    if (error) throw error;

    const stats = {
      total: data?.length || 0,
      byStatus: {},
      byPriority: {}
    };

    data?.forEach(order => {
      stats.byStatus[order?.status] = (stats?.byStatus?.[order?.status] || 0) + 1;
      stats.byPriority[order?.priority] = (stats?.byPriority?.[order?.priority] || 0) + 1;
    });

    return stats;
  }
};

export default adminOrderLifecycleService;